// The search response, and the readings the panel takes of it.
//
// Nothing here renders. It exists so the two judgements the panel makes - what
// a response SAYS, and whether it says anything at all - are testable without a
// DOM, and so neither is spelled twice.

import type { IntelSourceResult } from '@/components/report/intelIndex';
import { asIntelSource, threatLabelOf } from '@/components/report/intelIndex';
import type { IntelSearchResponse } from '@/lib/api';

// Re-exported so this module is the panel's one import for everything it needs
// to read a response. The labels themselves live with the narrower, shared with
// the report pane - two surfaces disagreeing about what an outcome is CALLED is
// two surfaces making different claims.
export { INTEL_OUTCOME_LABELS } from '@/components/report/intelIndex';

/**
 * Why the external feed was not asked, or '' when it was.
 *
 * Four states rather than a boolean, mirroring the four outcomes a lookup has,
 * and for the same reason: an empty source table means something different in
 * each of them. Reading them as one "nothing found" is how an unpaid
 * subscription renders as a clean bill of health.
 */
export type IntelSkipReason = '' | 'already_analysed' | 'not_configured' | 'provider_unavailable';

const SKIP_REASONS = new Set<string>([
  'already_analysed',
  'not_configured',
  'provider_unavailable',
]);

/** The skip reason, or '' for anything this build does not recognise - an
 *  unknown reason is not evidence that the feed answered. */
export function skipReasonOf(response: IntelSearchResponse): IntelSkipReason {
  return SKIP_REASONS.has(response.skipped) ? (response.skipped as IntelSkipReason) : '';
}

/** One source's answer, narrowed at the trust boundary the report shares. */
export function intelSources(response: IntelSearchResponse): IntelSourceResult[] {
  return response.sources
    .map((row) => asIntelSource(row))
    .filter((s): s is IntelSourceResult => s !== null);
}

/** What the card states above the source table. */
export interface IntelHeadline {
  /** How many distinct sources hold a record, each counted once.
   *
   *  A COUNT rather than the names. Which feeds this platform subscribes to is
   *  not something a report needs to disclose to read correctly, and naming
   *  them turns every panel into an inventory of our suppliers. The count is
   *  what carries the meaning: it is the numerator of "2 of 4". */
  reported: number;
  /** How many sources produced a real answer either way. The denominator of
   *  what follows, and never the number of sources on the response - a source
   *  that was never asked is not part of a ratio. */
  answered: number;
  /** How many sources could have answered for this kind of identifier. */
  total: number;
  /** The provider's own words for what it holds - a family, a signature, a
   *  threat class. Null when nothing matched, or when a match named nothing. */
  threatLabel: string | null;
  /** How many lookups failed. Not a negative result: an unknown one. */
  failed: number;
}

export function intelHeadline(sources: IntelSourceResult[]): IntelHeadline {
  const seen = new Set<string>();
  let answered = 0;
  let failed = 0;
  for (const source of sources) {
    if (source.outcome === 'match' || source.outcome === 'clean') answered += 1;
    if (source.outcome === 'error') failed += 1;
    if (source.outcome !== 'match') continue;
    // A source may answer with several records for one observable, and counting
    // each record would report one source twice.
    seen.add(source.source);
  }
  return {
    reported: seen.size,
    answered,
    total: sources.length,
    threatLabel: threatLabelOf(sources),
    failed,
  };
}

/**
 * The sentence the card leads with, and the tone it is said in.
 *
 * `tone` is deliberately never the verdict palette for a feed record. A record
 * says a third party reported this identifier before; it does not say this
 * sample is malicious, and colouring it red would assert something the row
 * beside it does not support. A LOCAL analysis is different - that is this
 * platform's own observation - and the rows that carry it wear the verdict
 * colours they earned.
 */
export interface IntelVerdictLine {
  headline: string;
  detail: string;
  tone: 'reported' | 'neutral' | 'warning';
}

export function intelVerdictLine(
  response: IntelSearchResponse,
  headline: IntelHeadline,
): IntelVerdictLine {
  const skip = skipReasonOf(response);
  if (skip === 'already_analysed') {
    return {
      headline: 'Analysed here',
      detail:
        'This deployment has already detonated this file, so no external feed was asked about it.',
      tone: 'reported',
    };
  }
  if (skip === 'not_configured') {
    return {
      headline: 'No external intelligence',
      detail:
        'This deployment holds no threat-intelligence credentials, so nothing outside it was asked. This is not a clean result.',
      tone: 'warning',
    };
  }
  if (skip === 'provider_unavailable') {
    return {
      headline: 'Provider unavailable',
      detail:
        'The intelligence provider did not answer, so nothing outside this deployment was checked. This is a platform failure, not a finding.',
      tone: 'warning',
    };
  }
  if (!response.reachable) {
    return {
      headline: 'No answer',
      detail: `The provider answered none of ${headline.total} lookups, so this identifier was not checked.`,
      tone: 'warning',
    };
  }
  if (headline.reported > 0) {
    const holds = headline.reported === 1 ? 'holds' : 'hold';
    return {
      headline: `Reported by ${headline.reported} of ${headline.answered}`,
      detail: `${headline.reported} of the ${headline.answered} sources that answered ${holds} a record for this identifier.`,
      tone: 'reported',
    };
  }
  return {
    headline: 'No record',
    detail:
      headline.failed > 0
        ? `None of the ${headline.answered} sources that answered hold a record; ${headline.failed} lookup(s) failed and say nothing either way.`
        : `None of the ${headline.answered} sources that answered hold a record for this identifier.`,
    tone: 'neutral',
  };
}

/** One fact about the sample, and every distinct value the records gave it. */
export interface IntelFact {
  label: string;
  values: string[];
}

// Display order only - every field a record carries is shown either way. A
// family answers what the search asked; a hash restates what is already printed
// at the top of the card.
const FACT_ORDER = [
  'malware_printable',
  'malware',
  'signature',
  'threat_type',
  'threat_type_desc',
  'tags',
  'yara_rules',
  'rule_name',
  'clamav',
  'file_name',
  'file_type',
  'file_size',
  'delivery_method',
  'first_seen',
  'last_seen',
  'reporter',
  'origin_country',
  'confidence_level',
];

// Shown last: they restate the searched identifier, or are pivot-only hashes.
const FACT_TRAILING = [
  'md5',
  'sha1',
  'sha256',
  'sha3_384',
  'imphash',
  'ssdeep',
  'tlsh',
  'telfhash',
  'gimphash',
  'dhash_icon',
  'humanhash',
  'magika',
  'trid',
];

/** Strips the `#1.` / `#2.` the narrower prefixes onto a multi-record answer,
 *  so one source reporting twice folds into one field rather than two. */
function factKey(key: string): string {
  const cut = key.lastIndexOf('.');
  return cut === -1 ? key : key.slice(cut + 1);
}

function rank(key: string): number {
  const lead = FACT_ORDER.indexOf(key);
  if (lead !== -1) return lead;
  const tail = FACT_TRAILING.indexOf(key);
  if (tail !== -1) return FACT_ORDER.length + 100 + tail;
  return FACT_ORDER.length;
}

/**
 * Every fact the matching records carry, folded into one list.
 *
 * The feeds cross-reference each other, so one sample comes back from several
 * carrying the same hashes and family. Per source that reads as several
 * findings, so a field is shown once with every distinct value it was given.
 *
 * Only matched records contribute - a source holding no record says nothing
 * about the file.
 */
export function mergedFacts(sources: readonly IntelSourceResult[]): IntelFact[] {
  const byKey = new Map<string, Set<string>>();
  for (const source of sources) {
    if (source.outcome !== 'match') continue;
    for (const fact of source.facts) {
      const key = factKey(fact.key);
      const value = fact.value.trim();
      if (key === '' || value === '') continue;
      const values = byKey.get(key);
      if (values) values.add(value);
      else byKey.set(key, new Set([value]));
    }
  }
  return [...byKey.entries()]
    .map(([key, values]) => ({ label: labelOfKey(key), values: [...values], key }))
    .sort((a, b) => rank(a.key) - rank(b.key))
    .map(({ label, values }) => ({ label, values }));
}

/** A wire field name as a reader would write it: `first_seen` → `First seen`. */
function labelOfKey(key: string): string {
  const words = key.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** What kind of identifier the query turned out to be, in the analyst's words
 *  rather than the wire's. */
export function intelKindLabel(kind: string, digest: string): string {
  if (kind === 'hash') return digest ? digest.replace('_', '-').toUpperCase() : 'Hash';
  if (kind === 'url') return 'URL';
  if (kind === 'host') return 'Host';
  return kind;
}
